import React, { useState, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { httpGetPortfolioSummaryTable} from "services/portfolios"
import { set } from 'date-fns';
import { useAuthContext } from "contexts/AuthContext"
import { is } from 'date-fns/locale';
import { chartPoints } from "./marketValueChartPoints";



// Register required chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const readThemeCssVar = (selector, varName, fallback) => {
  if (typeof document === "undefined") return fallback;
  const el = selector ? document.querySelector(selector) : null;
  const node = el || document.documentElement;
  const v = getComputedStyle(node).getPropertyValue(varName).trim();
  return v || fallback;
};

const MarketValueChart = ({
  portfolio,
  portfolio_list,
  related_model_id = null,
  related_model_type = null,
  sectors = null,
  date__gte,
  date__lte = null,
  week_day,
  setChartData,
  rows = null,
  type = "percentage_change",
  currency = null,
  aspectRatio = null,
  themeHostSelector = null,
  liveMarketValue = null,
  statusDate = null,
}) => {
  const { myUser, getMyUser, loadingMyUser, accountUserType } = useAuthContext()
  const userLanguage = myUser.language || 'default';

  const [data, setData] = useState([]);
  const [chartDataLocal, setChartDataLocal] = useState();
  const [chartOptionsLocal, setChartOptionsLocal] = useState();

  // Incomplete days dropped, the status date point set to the live header value when given.
  const points = useMemo(
    () => chartPoints(data, { liveMarketValue, statusDate }),
    [data, liveMarketValue, statusDate]
  );

  const { isSingleMonth, isSingleWeek, isSingleYear, isManyYears } = useMemo(() => {
    if (points.length === 0) {
      return { isSingleMonth: false, isSingleWeek: false, isSingleYear: false, isManyYears: false };
    }
    const firstDate = new Date(points[0].date);
    const lastDate = new Date(points[points.length - 1].date);
    const days = (lastDate - firstDate) / (1000 * 60 * 60 * 24);
    return {
      isSingleMonth: days <= 27,
      isSingleWeek: days <= 6, // Difference in days is 7 or less
      isSingleYear: days >= 365 && days <= 730, // Data period is equal or over 1 year
      isManyYears: days > 730, // Data period is over 2 years
    };
  }, [points]);


  const portfolioIds = useMemo(() => {
    if (portfolio != null) {
      return Array.isArray(portfolio) ? portfolio : [portfolio];
    }
    if (portfolio_list == null) return [];
    return Array.isArray(portfolio_list) ? portfolio_list : [portfolio_list];
  }, [portfolio, portfolio_list]);

  const portfolioIdsKey = portfolioIds.join(',');

  const investmentTypeIds = useMemo(
    () => related_model_type?.filter((item) => item.id !== "all").map((item) => item.id),
    [related_model_type]
  );

  const investmentTypeKey = (investmentTypeIds || []).join(',');

  useEffect(() => {
    if (!portfolioIds.length) {
      return;
    }

    const params = {
      portfolio: portfolioIds,
      related_model_id: related_model_id,
      sectors: sectors,
      investmentType: investmentTypeIds?.length ? investmentTypeIds : undefined,
      date__gte: date__gte,
      date__lte: date__lte,
      week_day: week_day,
      currency: currency,
    };

    httpGetPortfolioSummaryTable(params).then((response) => {
      setData(response?.data?.chart_data || []);
      if (setChartData) {
        setChartData(response?.data?.sub_data || {});
      }

    });
  }, [portfolioIdsKey, related_model_id, sectors, investmentTypeKey, date__gte, date__lte, week_day, currency, setChartData]);


  const get_chartDataDefault = () => {
    const lineColor = themeHostSelector
      ? readThemeCssVar(themeHostSelector, "--fp-chart-line", "#525f7f")
      : "#525f7f";
    const fillTop = themeHostSelector
      ? readThemeCssVar(themeHostSelector, "--fp-chart-fill-top", "rgba(23, 54, 229, 0.35)")
      : null;
    const fillBottom = themeHostSelector
      ? readThemeCssVar(themeHostSelector, "--fp-chart-fill-bottom", "rgba(23, 54, 229, 0)")
      : null;

    const areaFill = themeHostSelector
      ? (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            return fillTop || lineColor;
          }
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, fillTop);
          gradient.addColorStop(1, fillBottom);
          return gradient;
        }
      : lineColor;

    // Prepare the data for the chart
    const chartDataDefault = {
      // labels: data.map(item => new Date(item.date).toLocaleString('default', { month: 'short' })), // Month names
      // labels: data.map(item => {
      //   const date = new Date(item.date);
      //   if (isSingleWeek) {
      //     return date.toLocaleString('default', { weekday: 'short' });  // Show day names if data is within a single week
      //   } else if (isSingleMonth) {
      //     // return date.toLocaleString('default'); 
      //     return `Week ${Math.ceil(date.getDate() / 7)}`;  // Show weeks if data is within a single month
      //   } else {
      //     return date.toLocaleString('default', { month: 'short' });  // Show month names otherwise
      //   }
      // }),

      labels: points.map((item, index) => {
        const date = new Date(item.date);
      
        if (isSingleWeek) {
          // Show day names if data is within a single week
          return date.toLocaleDateString(userLanguage, { weekday: 'short' });
        } else if (isSingleMonth) {
          // Show one label per week if data is within 30 days
          const currentWeek = Math.ceil(date.getDate() / 7);
          const previousWeek = index > 0 ? Math.ceil(new Date(points[index - 1].date).getDate() / 7) : null;
          // Only return the label for the first day of each new week
          return currentWeek !== previousWeek ? `Week ${currentWeek}` : '';
        } else if (isManyYears) {
          // Show year labels if data spans over 2 years
          const currentYear = date.getFullYear();
          const previousYear = index > 0 ? new Date(points[index - 1].date).getFullYear() : null;
          // Only return the label for the first day of each new year
          return currentYear !== previousYear ? currentYear.toString() : '';
        } else if (isSingleYear) {
          // Show month names if data spans multiple months
          const currentMonth = date.toLocaleString(userLanguage, { month: '2-digit' });
          const previousMonth = index > 0 ? new Date(points[index - 1].date).toLocaleString(userLanguage, { month: '2-digit' }) : null;
          // Only return the label for the first day of each new month
          return currentMonth !== previousMonth ? currentMonth : '';
        } else {
          // Show month names if data spans multiple months
          const currentMonth = date.toLocaleString(userLanguage, { month: 'short' });
          const previousMonth = index > 0 ? new Date(points[index - 1].date).toLocaleString(userLanguage, { month: 'short' }) : null;
          // Only return the label for the first day of each new month
          return currentMonth !== previousMonth ? currentMonth : '';
        }
      }),
      datasets: [
        // {
        //   label: 'Market Value %',
        //   data: data.map(item => item.total_market_value_change_percent),
        //   // data: data.map(item => item.total_market_value),
        //   borderColor: '#525f7f',
        //   backgroundColor: '#525f7f',
        //   fill: false,
        //   tension: 0.01,
        //   pointRadius: 0, // Reduce the size of the data points
        // },
        {
          label: 'Market Value',
          // data: data.map(item => item.total_market_value_change_percent),
          data: points.map(item => item.total_market_value),
          borderColor: lineColor,
          backgroundColor: themeHostSelector ? areaFill : lineColor,
          fill: Boolean(themeHostSelector),
          tension: 0.1,
          pointRadius: 0, // Reduce the size of the data points
        },
      ],
    };
  return chartDataDefault;
  }

    useEffect(() => {
      setChartDataLocal(get_chartDataDefault());
    }, [points, aspectRatio, isSingleMonth, isSingleWeek, isSingleYear, isManyYears, themeHostSelector]);

  const get_chartOptionsDefault = () => {
    const gridMinor = themeHostSelector
      ? readThemeCssVar(themeHostSelector, "--fp-chart-grid", "rgba(0, 0, 0, 0.1)")
      : "rgba(0, 0, 0, 0.1)";
    const zeroLine = themeHostSelector
      ? readThemeCssVar(themeHostSelector, "--fp-chart-zero-line", "#FDCD3F")
      : "#FDCD3F";

    const chartOptionsDefault = {
      scales: {
        x: {
          title: {
            display: false,
            text: 'Month',
          },
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            minRotation: isSingleYear ? 45 : 0,
            maxRotation: isSingleYear ? 45 : 0,

          
            // This will only display unique month names on the x-axis
            // callback: function(value, index, values) {
            //   // Show only the first instance of each month
            //   const currentMonth = new Date(data[index].date).toLocaleString('default', { month: 'short' });
            //   const previousMonth = index > 0 ? new Date(data[index - 1].date).toLocaleString('default', { month: 'short' }) : null;
            //   return currentMonth !== previousMonth ? currentMonth : '';
            // },
            callback: function(value, index, values) {
              const currentDate = new Date(points[index].date);
              if (isSingleWeek) {
                // Display day names if within a single week
                return currentDate.toLocaleString(userLanguage, { weekday: 'short' });
                // return currentDate.toLocaleDateString(userLanguage, { day: 'numeric', month: 'numeric', year: 'numeric' })
              } else if (isSingleMonth) {
                // Display week numbers if within a single month
                // return currentDate.toLocaleString(userLanguage);
                // return `Week ${Math.ceil(currentDate.getDate() / 7)}`;
  
  
                // const currentWeek = `Week ${Math.ceil(currentDate.getDate() / 7)}`;
                // const previousWeek = index > 0 ? `Week ${Math.ceil(new Date(data[index - 1].date).getDate() / 7)}` : null;
                // // return currentWeek !== previousWeek ? currentWeek : '';
                // return currentWeek !== previousWeek ? currentDate.toLocaleDateString(userLanguage, { day: 'numeric', month: 'numeric' }) : '';



                const currentWeek = Math.ceil(currentDate.getDate() / 7);
                const previousWeek = index > 0 ? Math.ceil(new Date(points[index - 1].date).getDate() / 7) : null;
            
                // return currentWeek !== previousWeek ? currentDate.toLocaleDateString(userLanguage, { day: 'numeric', month: 'numeric' }) : '';
                // return index == 0 ? currentDate.toLocaleDateString(userLanguage, { day: 'numeric', month: 'numeric' }) : '';
                return (index === 0 || index % 7 === 0) ? currentDate.toLocaleDateString(userLanguage, { day: 'numeric', month: 'numeric' }) : '';

              } 
              else if (isManyYears) {
                // Display year labels if data spans over 2 years
                const currentYear = currentDate.getFullYear();
                const previousYear = index > 0 ? new Date(points[index - 1].date).getFullYear() : null;
                return currentYear !== previousYear ? currentYear.toString() : '';
                
              }
              else if (isSingleYear) {
                // Display unique month names if data spans multiple months
                const currentMonth = currentDate.toLocaleString(userLanguage, { month: 'short', year: '2-digit' });
                const previousMonth = index > 0 ? new Date(points[index - 1].date).toLocaleString(userLanguage, { month: 'short', year: '2-digit' }) : null;
                return (currentMonth !== previousMonth) ? currentMonth : '';
                
              } else {
                // Display unique month names if data spans multiple months
                const currentMonth = currentDate.toLocaleString(userLanguage, { month: 'short' });
                const previousMonth = index > 0 ? new Date(points[index - 1].date).toLocaleString(userLanguage, { month: 'short' }) : null;
                return (currentMonth !== previousMonth) ? currentMonth : '';

                
                              }
            },
            autoSkip: false,
          },
        },
        y: {
          grid: {
            display: true,
            color: (context) => {
              if (context.tick.value === 0) {
                return zeroLine;
              }
              return gridMinor;
            },
            borderDash: (context) => {
              if (context.tick.value === 0) {
                return [5, 5]; // dotted line
              }
              return [];
            },
            drawBorder: false,
          },
          ticks: {
            // Add '%' to each y-axis tick label
            callback: function(value) {
              // return `${value}`;
              return value.toFixed(2); // Ensure two decimal places
            },
          },
          title: {
            display: false,
            text: 'Market Value (EUR)',
          },
        },
      },
      responsive: true,
      maintainAspectRatio: aspectRatio != null,
      aspectRatio: aspectRatio != null ? aspectRatio : undefined,
      plugins: {
        tooltip: {
          enabled: true,
        },
        legend: {
          display: false, // Hide the legend
        },
      },
    };

    return chartOptionsDefault;
  }

    useEffect(() => {
      setChartOptionsLocal(get_chartOptionsDefault());
    }, [points, aspectRatio, isSingleMonth, isSingleWeek, isSingleYear, isManyYears, themeHostSelector]);



  return (
    <>
    <div style={{ height: '100%', width: '100%' }}>   {/* <h2>Market Value Over Time</h2> */}
      {/* <Line data={chartDataLocal? chartDataLocal:  chartDataDefault} options={chartOptionsLocal ? chartOptionsLocal: chartOptionsDefault} /> */}
      {

      chartDataLocal && <Line data={chartDataLocal? chartDataLocal:  null} options={chartOptionsLocal ? chartOptionsLocal: null} />
      }
    </div>
    
    </>
  );
};

export default MarketValueChart;